import React, { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { Product } from "../../../../components/Reusable/type";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchDGCoinPlans } from "../../../../features/auth/store/dgcoinPlanSlice";
import { FloatingLabelSelect } from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";

import { DGCoinTableProps } from "./types";
import { formatCountries } from "../../../../components/Reusable/utils";
import { FcViewDetails } from "react-icons/fc";
import { FiEdit3 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { DGCoinPlan } from "../../../../features/auth/store/types/dgcoin-types";

export const DGCoinSearchFilter: React.FC<{
  onStatusFilter: (status: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onCreateNewProduct: (type: string) => void;
}> = ({ onStatusFilter, searchTerm, onSearchChange, onCreateNewProduct }) => {
  return (
    <div className="flex justify-between items-center border-b-2 border-[#0D848910] px-4 py-3">
      <div className="flex items-center gap-4">
        <FloatingLabelSelect
          name="status"
          placeholder="Filter by Status"
          value={""}
          onChange={(e) => onStatusFilter(e.target.value)}
          options={[
            { value: "", label: "All statuses" },
            { value: "on", label: "Active" },
            { value: "off", label: "Inactive" },
          ]}
        />
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Search by name or code"
          className="border dark:bg-[#0D848910] border-[#E5E5E5] rounded-lg px-3 py-2 text-sm"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <Button
          bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
          textColor="text-white"
          borderRadius="rounded-lg"
          className="px-5 py-2"
          onClick={() => onCreateNewProduct("dgcoin")}
        >
          Add DG Coin Plan
        </Button>
      </div>
    </div>
  );
};

export const DGCoinTable: React.FC<DGCoinTableProps> = ({
  dgcoinPlans,
  statusFilter,
  onDGCoinPlanClick,
  onDeleteClick,
  onEditClick,
  loading,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    pagination,
    lastPage: totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.dgcoinPlans);
  const { nextPageUrl, prevPageUrl } = pagination;

  const filtered = dgcoinPlans.filter((plan) =>
    statusFilter ? plan.status === statusFilter : true
  );

  const goToNextPage = () => {
    if (currentPage < totalPages && nextPageUrl !== null) {
      dispatch(fetchDGCoinPlans({ url: nextPageUrl }));
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1 && prevPageUrl !== null) {
      dispatch(fetchDGCoinPlans({ url: prevPageUrl }));
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto">
        <thead>
          <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
            <th className="px-6 text-left">Product</th>
            <th className="px-6 text-left">Pricing</th>
            <th className="px-6 text-left">Countries</th>
            <th className="px-6 text-left">Status</th>
            <th className="px-6 text-left">Actions</th>
          </tr>
        </thead>
        <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
          {loading ? (
            <tr className="border-t h-[80px]">
              <td colSpan={5} className="p-4 text-center">
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              </td>
            </tr>
          ) : filtered.length > 0 ? (
            filtered.map((plan) => (
              <tr
                key={plan.id}
                className="border-b border-[#FFFFFF1A] cursor-pointer hover:bg-gray-50 dark:hover:bg-[#FFFFFF08]"
              >
                <td
                  className="px-6 py-4 align-middle flex items-center gap-3"
                  onClick={() => onDGCoinPlanClick(plan as Product)}
                >
                  <img
                    src={plan.image_url || "/default-product.png"}
                    alt={plan.name}
                    className="w-12 h-12 rounded"
                  />
                  <div>
                    <div className="font-semibold text-sm">{plan.name}</div>
                    <div className="text-gray-500 text-xs">{plan.code}</div>
                  </div>
                </td>
                <td className="px-6 py-4 align-middle">
                  <div className="text-sm">
                    <div>Wallet: {plan.wallet_price}</div>
                    <div>API: {plan.api_price}</div>
                    <div>ATM: {plan.atm_price}</div>
                  </div>
                </td>
                <td className="px-6 py-4 align-middle">
                  {formatCountries(plan.countries)}
                </td>
                <td className="px-6 py-4 align-middle">
                  <span
                    className={`${
                      plan.status === "on" ? "text-green-600" : "text-red-600"
                    } text-sm`}
                  >
                    {plan.status === "on" ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 align-middle">
                  <div className="flex items-center justify-start space-x-3">
                    <button
                      className="cursor-pointer"
                      onClick={() => onDGCoinPlanClick(plan)}
                    >
                      <FcViewDetails size="20" />
                    </button>
                    <button
                      className="text-blue-600"
                      onClick={() => onEditClick(plan as DGCoinPlan)}
                    >
                      <FiEdit3 className="text-2xl" />
                    </button>
                    <button
                      className="text-red-600"
                      onClick={() => onDeleteClick(plan.id)}
                    >
                      <MdOutlineDelete className="text-2xl" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} className="p-4 text-center">
                No plans found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A] px-4 py-3">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] ${
            currentPage === totalPages || totalPages === 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          onClick={goToNextPage}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const DGCoinDetailView: React.FC<{
  dgcoinPlan: Product;
  onBack: () => void;
}> = ({ dgcoinPlan, onBack }) => {
  return (
    <div className="p-6">
      <button onClick={onBack} className="text-blue-600 mb-4 underline text-sm">
        Back to list
      </button>
      <div className="flex gap-6">
        <img
          src={dgcoinPlan.image_url || "/api/placeholder/400/200"}
          alt={dgcoinPlan.name}
          className="w-48 h-48 rounded"
        />
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">{dgcoinPlan.name}</h2>
          <p className="text-gray-500">{dgcoinPlan.message}</p>
          <p>
            Status:{" "}
            <span
              className={
                dgcoinPlan.status === "on" ? "text-green-600" : "text-red-600"
              }
            >
              {dgcoinPlan.status === "on" ? "Active" : "Inactive"}
            </span>
          </p>
          <p>Wallet price: {dgcoinPlan.wallet_price}</p>
          <p>API price: {dgcoinPlan.api_price}</p>
          <p>ATM price: {dgcoinPlan.atm_price}</p>
          <p>Countries: {formatCountries(dgcoinPlan.countries)}</p>
        </div>
      </div>
    </div>
  );
};
